import Tabs from "devextreme-react/tabs";
import { useContext, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useGetCompany } from "../../../../api/company";
import { useTitle } from "../../../../contexts/title";
import { AR, BusinessFlow, OrderStatus, ProductLine, ROType } from "../../../../types/enums";
import { RetailOrderContext } from "../context/RetailOrderContext";
import { RetailOrderProgressStepInfo } from "../types/RetailOrderProgressStepInfo";

export default function RetailOrderProgress() {

	const { order } = useContext(RetailOrderContext);
	const dealer = useGetCompany(order.details.dealerCompanyID);

	const location = useLocation();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const returnUrl = encodeURIComponent(params.get("returnUrl") ?? "");

	const resolvedIcon = 'dx-icon dx-icon-Inscyth-Checked-Box';
	const unresolvedIcon = '';

	var steps: RetailOrderProgressStepInfo[] =
		[
			{
				text: 'Info',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID,
				visible: true,
				disabled: false,
				icon: 'dx-icon dx-icon-info'
			},
			{
				text: (order.details.roTypeID === ROType.Measure || order.details.roTypeID === ROType.CabinetMeasureOrder) ?
					'Measure' : 'Measure/Template',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/110',
				visible: order.details.productLineID !== ProductLine.LaminateDelivered &&
					order.details.roTypeID !== ROType.CabinetInstallOrder && order.details.roTypeID !== ROType.Plumbing,
				disabled: false,
				icon: order.details.resolvedARs.includes(AR.CfiToEnterTemplateDate) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Actual Measure',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/115',
				visible: (dealer.data?.businessFlowID === BusinessFlow.Lowes && order.details.roTypeID === ROType.Measure) ||
					order.details.roTypeID === ROType.CabinetMeasureOrder,
				disabled: !order.details.resolvedARs.includes(AR.CfiToEnterTemplateDate),
				icon: order.details.resolvedARs.includes(AR.CfiToEnterActualMeasureDate) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Schedule',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/116',
				visible: (order.details.roTypeID === ROType.Plumbing && order.details.activeARs.includes(AR.ScheduleDisconnect)),
				disabled: false,
				icon: order.details.resolvedARs.includes(AR.ScheduleReconnect) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Schedule',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/119',
				visible: (order.details.roTypeID === ROType.Plumbing && !order.details.activeARs.includes(AR.ScheduleDisconnect)),
				disabled: false,
				icon: order.details.resolvedARs.includes(AR.ScheduleReconnect) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Design',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/134',
				visible: order.details.roTypeID === ROType.CabinetMeasureOrder,
				disabled: !order.details.resolvedARs.includes(AR.CfiToEnterActualMeasureDate),
				icon: order.details.resolvedARs.includes(AR.DealerToSubmitCabinetDesign) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Checklist',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/117',
				visible: (order.details.roTypeID === ROType.Retail || order.details.roTypeID === ROType.AddOn) &&
					order.details.isWren,
				disabled: !order.details.resolvedARs.includes(AR.CfiToEnterTemplateDate),
				icon: order.details.resolvedARs.includes(AR.CfiToUploadTemplateChecklist) ? resolvedIcon : unresolvedIcon
			},
			{
				text: order.details.roTypeID === ROType.CabinetMeasureOrder || order.details.roTypeID === ROType.Plumbing ? 'Change Order' : 'Confirm',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/120',
				visible: order.details.roTypeID !== ROType.Display && order.details.roTypeID !== ROType.DisplayAdmin &&
					order.details.roTypeID !== ROType.CabinetInstallOrder,
				disabled:
					(!order.details.resolvedARs.includes(AR.CfiToConfirmOrChangeOrder) &&
						!order.details.activeARs.includes(AR.CfiToConfirmOrChangeOrder) &&
						!order.details.activeARs.includes(AR.DealerToConfirmChangeOrder)),
				icon: order.details.resolvedARs.includes(AR.CfiToConfirmOrChangeOrder) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Order Material',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/130' +
					(order.details.materialOrders?.length === 0 ? '/new' : ''),
				visible: order.details.roTypeID !== ROType.Measure && order.details.roTypeID != ROType.CabinetInstallOrder &&
					order.details.roTypeID != ROType.CabinetMeasureOrder && order.details.roTypeID !== ROType.Plumbing,
				disabled: !((order.details.orderStatusID === OrderStatus.Open && !order.details.resolvedARs.includes(AR.CfiToEnterActualInstallDate)) ||
					(order.details.orderStatusID === OrderStatus.Hold && order.details.unrestrictedMO) ||
					order.details.materialOrders?.length > 0),
				icon: (order.details.materialOrders?.length > 0)
					? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Schedule',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/143',
				visible: order.details.roTypeID !== ROType.Measure && order.details.roTypeID !== ROType.CabinetMeasureOrder &&
					order.details.roTypeID !== ROType.Plumbing,
				disabled:
					(!order.details.resolvedARs.includes(AR.CfiToEnterScheduledInstallDate) &&
						!order.details.activeARs.includes(AR.CfiToEnterScheduledInstallDate) &&
						!order.details.activeARs.includes(AR.CfiToPrintPackingSlip)),
				icon: order.details.resolvedARs.includes(AR.CfiToEnterScheduledInstallDate) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Packing Slip',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/133',
				visible: order.details.isDeliveryOnly,
				disabled:
					(!order.details.resolvedARs.includes(AR.CfiToPrintPackingSlip) &&
						!order.details.activeARs.includes(AR.CfiToPrintPackingSlip)),
				icon: order.details.resolvedARs.includes(AR.CfiToPrintPackingSlip) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'PO Received Date',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/137',
				visible: order.details.roTypeID === ROType.CabinetInstallOrder,
				disabled:
					!order.details.resolvedARs.includes(AR.DealerToEnterCabinetPOReceivedDate) &&
					!order.details.activeARs.includes(AR.DealerToEnterCabinetPOReceivedDate),
				icon: order.details.resolvedARs.includes(AR.DealerToEnterCabinetPOReceivedDate) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Quality Check',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/138',
				visible: order.details.roTypeID === ROType.CabinetInstallOrder,
				disabled:
					(!order.details.resolvedARs.includes(AR.InstallerToVerifyCabinetQuality) &&
						!order.details.activeARs.includes(AR.InstallerToVerifyCabinetQuality)),
				icon: order.details.resolvedARs.includes(AR.InstallerToVerifyCabinetQuality) ? resolvedIcon : unresolvedIcon
			},
			{
				text: order.details.isDeliveryOnly ? 'Delivery Date' : 'Install Date',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/145',
				visible: order.details.roTypeID !== ROType.Measure && order.details.roTypeID !== ROType.CabinetMeasureOrder &&
					order.details.roTypeID !== ROType.Plumbing,
				disabled:
					(!order.details.resolvedARs.includes(AR.CfiToEnterActualInstallDate) &&
						!order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) &&
					!order.details.resolvedARs.includes(AR.InstallerToEnterActualInstallStartDate) &&
						!order.details.activeARs.includes(AR.InstallerToEnterActualInstallStartDate) &&
					!order.details.resolvedARs.includes(AR.InstallerToEnterLowerCabinetsPlumbLevelDate) &&
						!order.details.activeARs.includes(AR.InstallerToEnterLowerCabinetsPlumbLevelDate) &&
					!order.details.resolvedARs.includes(AR.InstallerToEnterActualInstallCompletionDate) &&
						!order.details.activeARs.includes(AR.InstallerToEnterActualInstallCompletionDate)),
				icon: order.details.resolvedARs.includes(AR.CfiToEnterActualInstallDate) ||
					order.details.resolvedARs.includes(AR.InstallerToEnterActualInstallCompletionDate) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Photos',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/147',
				visible: (order.details.roTypeID === ROType.Retail || order.details.roTypeID === ROType.AddOn) &&
					order.details.isWren,
				disabled: !order.details.resolvedARs.includes(AR.CfiToEnterActualInstallDate),
				icon: order.details.resolvedARs.includes(AR.CfiToUploadInstallationPhotos) ? resolvedIcon : unresolvedIcon
			},
			{
				text: 'Upload Files',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/160',
				visible: order.details.roTypeID !== ROType.CabinetMeasureOrder && order.details.roTypeID !== ROType.CabinetInstallOrder &&
					order.details.roTypeID !== ROType.Plumbing,
				disabled:
					(!order.details.resolvedARs.includes(AR.CfiToUploadCocInvoice) &&
						!order.details.activeARs.includes(AR.CfiToUploadCocInvoice)),
				icon: order.details.resolvedARs.includes(AR.CfiToUploadCocInvoice) ? resolvedIcon : unresolvedIcon
			}
			,
			{
				text: 'Upload Verification Form',
				navigateTo: '/cfi/retail-order/' + order.details.retailOrderID + '/162',
				visible: order.details.roTypeID === ROType.CabinetInstallOrder || order.details.roTypeID === ROType.Plumbing,
				disabled:
					(!order.details.resolvedARs.includes(AR.InstallerToUploadVerificationForm) &&
						!order.details.activeARs.includes(AR.InstallerToUploadVerificationForm)),
				icon: order.details.resolvedARs.includes(AR.InstallerToUploadVerificationForm) ? resolvedIcon : unresolvedIcon
			}
		].filter(x => x.visible);

	let selectedStep: RetailOrderProgressStepInfo | undefined;

	steps.forEach(step => {
		if (location.pathname.includes(step.navigateTo) && (!selectedStep || selectedStep.navigateTo < step.navigateTo)) {
			selectedStep = step;
		}
	});

	const pageTitle = useTitle();
	useEffect(() => {
		if (selectedStep) {
			pageTitle.setTitle(selectedStep?.text)
		}
	}, [pageTitle, selectedStep]);

	return (
		<Tabs
			className="order-progress"
			dataSource={steps}
			selectedItem={selectedStep}
			onItemClick={(e) => navigate(e.itemData?.navigateTo! + (!!returnUrl ? '?returnUrl=' + returnUrl : ''))}
		>

		</Tabs>
	);
}
